<template>
  <picture
    :class="pictureClass"
    :style="pictureStyle"
  >
    <source
      v-for="source in orderedSources"
      :key="source.type"
      :srcset="source.srcset"
      :type="source.type"
    >
    <img
      :alt="altText"
      v-bind="forwardedAttrs"
      :class="props.imageClass"
      :style="props.imageStyle"
      :src="props.picture.fallback"
      :loading="props.loading"
    >
  </picture>
</template>

<script lang="ts" setup>
import type { PictureSources } from '@/types/Media'
import type { StyleValue } from 'vue'
import { computed, useAttrs } from 'vue'

const props = withDefaults(defineProps<{
  picture: PictureSources
  alt: string
  pictureClass?: string | Record<string, boolean> | Array<string | Record<string, boolean>>
  pictureStyle?: StyleValue
  imageClass?: string | Record<string, boolean> | Array<string | Record<string, boolean>>
  imageStyle?: StyleValue
  loading?: 'lazy' | 'eager'
}>(), {
  loading: 'lazy' // Default to lazy loading for better performance
})

defineOptions({ inheritAttrs: false })

const attrs = useAttrs() as Record<string, unknown>

const pictureClass = computed<string | Record<string, boolean> | Array<string | Record<string, boolean>> | undefined>(() => {
  const classes: Array<string | Record<string, boolean>> = []
  if (props.pictureClass) {
    if (Array.isArray(props.pictureClass)) {
      classes.push(...props.pictureClass)
    } else {
      classes.push(props.pictureClass)
    }
  }
  if (attrs.class) {
    classes.push(attrs.class as string | Record<string, boolean>)
  }
  if (classes.length === 0) return undefined
  if (classes.length === 1) return classes[0]
  return classes
})

const pictureStyle = computed<StyleValue>(() => {
  const styles: StyleValue[] = []
  if (props.pictureStyle) styles.push(props.pictureStyle)
  if (attrs.style) styles.push(attrs.style as StyleValue)
  if (styles.length === 0) return undefined
  if (styles.length === 1) return styles[0]
  return styles
})

// Only forward attrs that should go to img (exclude class/style which are handled separately)
const forwardedAttrs = computed(() => {
  const { class: _class, style: _style, ...rest } = attrs as Record<string, unknown>
  return rest
})

const orderedSources = computed(() => props.picture.sources)
const altText = computed(() => props.alt)
</script>
